import { JamEventDetails } from '@/src/types/JamEventDetails';
import { JamEventLogEntry } from '@/src/types/JamEventLogEntry';
import { LeaderboardTeam } from '@/src/types/JamLeaderboard';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, SpaceBetween, Table } from '@amzn/awsui-components-react';
import { TFunction } from 'i18next';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

enum textStatus {
  SUCCESS = 'text-status-success',
  ERROR = 'text-status-error',
  INFO = 'text-status-info',
}

export const getCannedColor = (actionType: string, score: number, challengeMaxScore: number): textStatus => {
  switch (actionType) {
    case 'completed-task':
      return score > 0 ? textStatus.SUCCESS : textStatus.ERROR;
    case 'completed-challenge':
      if (score === challengeMaxScore) {
        return textStatus.SUCCESS;
      }
      return score > 0 ? textStatus.INFO : textStatus.ERROR;
    case 'answered-incorrectly':
      return textStatus.ERROR;
    default:
      return textStatus.INFO;
  }
};

export const getCannedMessage = (
  event: JamEventDetails,
  score: number,
  challengeMaxScore: number,
  challengeId: string,
  taskId: string,
  actionType: string,
  hash: number,
  t: TFunction
): string => {
  const challenge = event?.challenges.find((chal) => chal.id === challengeId);

  const challengeTitle = challenge ? challenge.title : 'Unknown Challenge';

  const scoreText = `${score}/${challengeMaxScore}`;

  let cannedDict;
  switch (actionType) {
    case 'completed-challenge':
      let completedStatusKey = 'none';
      if (score === challengeMaxScore) {
        completedStatusKey = 'max';
      } else if (score > 0) {
        completedStatusKey = 'partial';
      }

      cannedDict = (i18nKeys.jamDashboard.leaderboard.eventLog.canned.completed as any)[completedStatusKey];
      break;
    case 'completed-task':
      if (score > 0) {
        cannedDict = i18nKeys.jamDashboard.leaderboard.eventLog.canned.completed.task;
      } else {
        cannedDict = i18nKeys.jamDashboard.leaderboard.eventLog.canned.completed.none;
      }
      break;
    case 'answered-incorrectly':
      cannedDict = i18nKeys.jamDashboard.leaderboard.eventLog.canned.incorrect;
      break;
    case 'requested-clue':
      cannedDict = i18nKeys.jamDashboard.leaderboard.eventLog.canned.clue;
      break;
    case 'started-challenge':
      cannedDict = i18nKeys.jamDashboard.leaderboard.eventLog.canned.started;
      break;
    default:
      return '';
  }
  const dictKeys = Object.keys(cannedDict);
  const dictIndex = dictKeys[hash % dictKeys.length];

  return t(cannedDict[dictIndex], { challenge: challengeTitle, points: scoreText, task: taskId });
};

export const getCurrentTeamDisplayName = (allTeams: LeaderboardTeam[], targetTeamName: string) => {
  const teamIndex = allTeams.findIndex((team: LeaderboardTeam) => {
    return targetTeamName === team.teamName;
  });
  if (teamIndex === -1) {
    return targetTeamName;
  }
  return allTeams[teamIndex].teamDisplayName;
};

export interface EventLogProps {
  event: JamEventDetails;
  eventLogs: JamEventLogEntry[];
  isLoading: boolean;
  showOriginalTeamName: boolean;
  teams: LeaderboardTeam[];
}

export const EventLog: React.FC<EventLogProps> = memo((props: EventLogProps) => {
  const { t } = useTranslation();
  const filteredEventLogs = useMemo(
    () =>
      props.eventLogs.reduce((accum: JamEventLogEntry[], entry) => {
        const { score, challengeScore, challengeId, taskNumber, actionType, hash } = entry;
        const message = getCannedMessage(
          props.event,
          score,
          challengeScore,
          challengeId,
          `${taskNumber}`,
          actionType,
          hash,
          t
        );
        if (!!message) {
          accum.push(Object.assign(entry, { message }));
        }
        return accum;
      }, []),
    [props.eventLogs]
  );
  return (
    <Table
      key="event-log"
      variant="embedded"
      columnDefinitions={[
        {
          id: 'When',
          header: (
            <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
              When
            </Box>
          ),
          cell: ({ dateTimeUTC, actionType, score, challengeScore }: JamEventLogEntry) => (
            <Box
              color={getCannedColor(actionType, score, challengeScore)}
              padding={{ left: 'm' }}
              variant="h4"
              tagOverride="span"
            >
              {new Date(dateTimeUTC).toLocaleString()}
            </Box>
          ),
        },
        {
          id: 'Who',
          header: (
            <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
              Who
            </Box>
          ),
          cell: ({ teamName, actionType, score, challengeScore }: JamEventLogEntry) => (
            <Box
              color={getCannedColor(actionType, score, challengeScore)}
              padding={{ left: 'm' }}
              variant="h4"
              tagOverride="span"
            >
              {props.showOriginalTeamName
                ? `${getCurrentTeamDisplayName(props.teams, teamName)} (${teamName})`
                : getCurrentTeamDisplayName(props.teams, teamName)}
            </Box>
          ),
        },
        {
          id: 'What',
          header: (
            <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
              What
            </Box>
          ),
          cell: ({ actionType, score, challengeScore, message }: JamEventLogEntry & { message: string }) => (
            <Box
              color={getCannedColor(actionType, score, challengeScore)}
              padding={{ left: 'm' }}
              variant="h4"
              tagOverride="span"
            >
              {message}
            </Box>
          ),
        },
      ]}
      items={filteredEventLogs}
      wrapLines
      sortingDisabled
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <b>{t(i18nKeys.jamDashboard.leaderboard.table.emptyMessage)}</b>
          </SpaceBetween>
        </Box>
      }
      loading={props.isLoading}
    />
  );
});
